import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  changePassword,
  fetchDevices,
  revokeDevice,
  MIN_PASSWORD_LENGTH,
  type DeviceView,
} from '../api/auth';
import {
  claimIdentity,
  fetchProfile,
  removeIdentity,
  updateProfile,
  type ProfileView,
} from '../api/profiles';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Avatar, IdentityPanel } from './IdentityPanel';
import { HelpMore } from './HelpMore';
import { useMeta } from './MetaContext';
import { DEVICE_ICONS, describeAgent, deviceTitle } from './deviceName';
import { Planned } from './Planned';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';
import { fmtDay, fmtMoment } from './dates';

/** A card heading with its icon, one per settings card. */
function Heading({ icon, children }: { icon: string; children: string }) {
  return (
    <h2>
      <i className={`bi bi-${icon}`} aria-hidden="true" /> {children}
    </h2>
  );
}

/** Account settings (UI doc section 4, `/settings`): the one place for name, identities, password, devices, session. */
export function Settings() {
  const { meta } = useMeta();
  const { me, refresh } = useAuth();
  const [search] = useSearchParams();
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [current, setCurrent] = useState('');
  const [fresh, setFresh] = useState('');
  const [passwordDone, setPasswordDone] = useState(false);
  const [devices, setDevices] = useState<DeviceView[]>([]);
  const hasPassword = me.identities.includes('PASSWORD');

  useEffect(() => {
    if (!me.authenticated || !me.handle) return;
    fetchProfile(me.handle)
      .then((p) => {
        setProfile(p);
        setDisplayName(p.displayName ?? '');
      })
      .catch((e) => setError(errorText(e)));
    fetchDevices()
      .then(setDevices)
      .catch(() => setDevices([]));
  }, [me.authenticated, me.handle]);

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    setPasswordDone(false);
    try {
      await changePassword(current, fresh);
      setCurrent('');
      setFresh('');
      setError(null);
      setPasswordDone(true);
      await refresh();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function revoke(id: string) {
    try {
      await revokeDevice(id);
      setDevices((d) => d.filter((x) => x.id !== id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    try {
      const p = await updateProfile({ displayName });
      setProfile(p);
      setError(null);
      setSaved(true);
      await refresh();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Shell title={ca.settings.title} width="narrow">
      {!me.authenticated && (
        <p className="muted">
          <Link to="/signin">{ca.signInToSee}</Link>
        </p>
      )}
      {error && (
        <p className="notice bad" role="alert">
          {error}
        </p>
      )}
      {me.authenticated && (
        <div className="settings-page">
          <form
            className="card form"
            onSubmit={(e) => void save(e)}
            aria-label={ca.settings.account}
          >
            <Heading icon="person">{ca.settings.account}</Heading>
            <div className="row person">
              <Avatar
                url={profile?.avatarUrl}
                name={profile?.displayName || me.displayName || me.handle || '?'}
                size={48}
              />
              <p className="muted small">@{me.handle}</p>
            </div>
            <label>
              {ca.settings.displayName}
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={64}
              />
            </label>
            <div className="row">
              <button type="submit" className="primary">
                {ca.settings.save}
              </button>
              {saved && <span className="muted small">{ca.settings.saved}</span>}
            </div>
          </form>
          <section className="card" aria-label={ca.settings.identities}>
            <Heading icon="door-open">{ca.settings.identities}</Heading>
            <IdentityPanel
              details={profile?.identityDetails ?? []}
              steamEnabled={meta.features.steam}
              discordEnabled={meta.features.discord}
              next="/settings"
              notices={{ steam: search.get('steam'), discord: search.get('discord') }}
              onClaim={(provider, subject) =>
                claimIdentity(provider, subject)
                  .then((p) => {
                    setProfile(p);
                    setError(null);
                  })
                  .catch((e) => setError(errorText(e)))
              }
              onRemove={(provider) =>
                removeIdentity(provider)
                  .then((p) => {
                    setProfile(p);
                    setError(null);
                  })
                  .catch((e) => setError(errorText(e)))
              }
            />
          </section>
          <form
            className="card form"
            onSubmit={(e) => void savePassword(e)}
            aria-label={ca.settings.password}
          >
            <Heading icon="lock">{ca.settings.password}</Heading>
            <HelpMore short={ca.settings.passwordIntro} more={ca.settings.passwordIntroMore} />
            <div className="row">
              <span className={`chip ${hasPassword ? 'ok' : ''}`}>
                {hasPassword ? ca.profileUi.passwordYes : ca.profileUi.passwordNo}
              </span>
            </div>
            {me.identities.length === 1 && hasPassword && (
              <p className="notice warn">{ca.settings.passwordOnlyWarning}</p>
            )}
            {hasPassword && (
              <label>
                {ca.settings.currentPassword}
                <input
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </label>
            )}
            <label>
              {ca.settings.newPassword}
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={fresh}
                onChange={(e) => setFresh(e.target.value)}
              />
              <span className="hint">{ca.passwordHint}</span>
            </label>
            <div className="row">
              <button type="submit" className="primary" disabled={fresh.length < 10}>
                {ca.settings.save}
              </button>
              {passwordDone && <span className="muted small">{ca.settings.passwordChanged}</span>}
            </div>
            <p className="muted small">{ca.settings.recoveryHint}</p>
          </form>
          <section className="card" aria-label={ca.settings.devices}>
            <Heading icon="laptop">{ca.settings.devices}</Heading>
            <HelpMore short={ca.settings.devicesIntro} more={ca.settings.devicesIntroMore} />
            <ul className="list">
              {devices.map((d) => {
                const about = d.userAgent ? describeAgent(d.userAgent) : null;
                return (
                  <li key={d.id} className="device-row">
                    <i
                      className={`bi bi-${DEVICE_ICONS[about?.kind ?? 'computer']}`}
                      aria-hidden="true"
                    />
                    <span className="grow">
                      <strong>{about ? deviceTitle(about) : d.address}</strong>
                      <span className="muted small device-detail">
                        {[
                          about ? ca.settings.deviceKinds[about.kind] : '',
                          ca.settings.signedIn(fmtDay(d.signedInAt)),
                          ca.settings.lastSeen(fmtMoment(d.lastSeenAt)),
                          about && d.address ? ca.settings.address(d.address) : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {d.current ? (
                      <span className="chip ok">{ca.settings.thisDevice}</span>
                    ) : (
                      <button type="button" className="small" onClick={() => void revoke(d.id)}>
                        {ca.settings.revoke}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
          <section className="card" aria-label={ca.settings.session}>
            <Heading icon="box-arrow-right">{ca.settings.session}</Heading>
            <p className="muted small">{ca.settings.signOutHint}</p>
            <div className="row actions">
              <Link className="button" to="/signout">
                {ca.nav.signOut}
              </Link>
            </div>
          </section>
          <Planned ids={['settings-password-recovery']} />
        </div>
      )}
    </Shell>
  );
}
